import { useEffect, useRef, useState } from 'react';
import { PRODUCT_URLS } from '@podus/utils';
import { useLanguage } from '@/i18n/LanguageContext';

const CARD_TOP_OFFSET = 20; // px peek between stacked cards
const CARD_SIDE_INSET = 16; // px inset per card level (left + right)
const SLIDE_IN_VH = 1.5; // fraction of viewport height for each card slide-in

/** Background color stops (RGB) — one per service card, subtle professional tones */
const WRAPPER_BG_COLORS: [number, number, number][] = [
    [255, 255, 255],
    [248, 249, 250], // neutral cool gray
    [255, 255, 255],
    [248, 249, 250], // neutral cool gray
    [255, 255, 255],
];

function lerpColor(a: [number, number, number], b: [number, number, number], t: number): string {
    return `rgb(${Math.round(a[0] + (b[0] - a[0]) * t)}, ${Math.round(a[1] + (b[1] - a[1]) * t)}, ${Math.round(a[2] + (b[2] - a[2]) * t)})`;
}

const services = [
    {
        brandPrefix: 'Podus AI',
        name: 'Legal Assistant',
        logo: '/logo/podus-ai.svg',
        image: '/services/legal_assistant.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Try Our Legal Assistant',
        ctaLink: PRODUCT_URLS.chatpodusai,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Checker',
        logo: '/logo/podus-cheaker.svg',
        image: '/services/checker.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Run a Background Check',
        ctaLink: PRODUCT_URLS.poduschecker,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Contracts',
        logo: '/logo/podus-contract.svg',
        image: '/services/contract.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Get Started For Free',
        ctaLink: PRODUCT_URLS.poduscontract,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Appointment',
        logo: '/logo/podus-appointment.svg',
        image: '/services/appointment.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Book an Appointment',
        ctaLink: PRODUCT_URLS.podusappointment,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Aid',
        logo: '/logo/podus-aids.svg',
        image: '/services/aids.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Request Aid',
        ctaLink: PRODUCT_URLS.podusaid,
        bgColor: '#f5f5f5',
    },
];

/** Find nearest scrollable ancestor */
function getScrollParent(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll') return parent;
        parent = parent.parentElement;
    }
    return null;
}

/** Fade-up wrapper triggered by IntersectionObserver — fires once */
function AnimateOnScroll({
    children,
    className = '',
    delay = 0,
}: {
    children: React.ReactNode;
    className?: string;
    delay?: number;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
            }}
        >
            {children}
        </div>
    );
}

/** Mobile service card — with viewport-triggered fade-in */
function MobileServiceCard({ service, index }: { service: (typeof services)[number]; index: number }) {
    const { t } = useLanguage();
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.3 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className="flex h-full flex-col rounded-2xl p-6"
            style={{
                backgroundColor: service.bgColor,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
                transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
        >
            {/* Icon + Name */}
            <div className="mb-4 flex items-center gap-3">
                <img
                    src={service.logo}
                    alt={`${service.brandPrefix} ${service.name} icon`}
                    className="h-10 w-10 flex-shrink-0 rounded-xl object-contain"
                />
                <h3 className="text-[24px] font-bold leading-[1.1] tracking-tight sm:text-[28px]">
                    <span className="text-secondary">{service.brandPrefix} </span>
                    <span className="text-[#AEAEAE]">{service.name}</span>
                </h3>
            </div>

            {/* Description */}
            <p className="text-secondary mb-6 flex-1 text-sm font-normal leading-relaxed">
                {t(`services.${index}.description`)}
            </p>

            {/* CTA Button */}
            <a
                href={service.ctaLink}
                className="bg-secondary rounded-full px-6 py-3 text-center text-sm font-medium text-white transition-all duration-200 active:scale-[0.98]"
            >
                {t(`services.${index}.cta`)}
            </a>
        </div>
    );
}

/** Mobile horizontal snap carousel */
function MobileCarousel() {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const cards = container.querySelectorAll<HTMLElement>('[data-carousel-card]');

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        const index = Number(entry.target.getAttribute('data-index'));
                        if (!isNaN(index)) setActiveIndex(index);
                    }
                }
            },
            { root: container, threshold: 0.5 },
        );

        cards.forEach((card) => observer.observe(card));
        return () => observer.disconnect();
    }, []);

    return (
        <div>
            {/* Scrollable carousel */}
            <div
                ref={scrollRef}
                className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-6"
                style={{ scrollPaddingInline: '16px' }}
            >
                {services.map((service, i) => (
                    <div key={i} data-carousel-card data-index={i} className="w-[calc(100vw-32px)] shrink-0 snap-start">
                        <MobileServiceCard service={service} index={i} />
                    </div>
                ))}
            </div>

            {/* Dot indicators */}
            <div className="mt-2 flex items-center justify-center gap-2">
                {services.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        aria-label={`Go to service ${i + 1}`}
                        className={`rounded-full transition-all duration-300 ${
                            activeIndex === i ? 'bg-primary h-2.5 w-8' : 'bg-secondary-50 h-2.5 w-2.5'
                        }`}
                        onClick={() => {
                            const card = scrollRef.current?.children[i] as HTMLElement | undefined;
                            card?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'center',
                            });
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

export default function Services() {
    const { t } = useLanguage();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const cardsContainerRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
    const activeCardRef = useRef(-1);
    const [containerHeight, setContainerHeight] = useState(0);
    const [wrapperHeight, setWrapperHeight] = useState(`${(services.length - 1) * 70 + 100}vh`);
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
    );
    const [isTallScreen, setIsTallScreen] = useState(() => typeof window !== 'undefined' && window.innerHeight > 750);

    /* Track lg breakpoint + screen height */
    useEffect(() => {
        const mql = window.matchMedia('(min-width: 1024px)');
        const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        mql.addEventListener('change', handler);

        const onResize = () => setIsTallScreen(window.innerHeight > 750);
        window.addEventListener('resize', onResize);

        return () => {
            mql.removeEventListener('change', handler);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    /* Scroll-driven stacking animation — desktop only */
    useEffect(() => {
        if (!isDesktop) {
            setWrapperHeight('auto');
            cardRefs.current.forEach((card) => {
                if (card) {
                    card.style.transform = '';
                    card.style.opacity = '';
                    delete card.dataset.active;
                }
            });
            contentRefs.current.forEach((el) => {
                if (el) el.style.transform = '';
            });
            activeCardRef.current = -1;
            if (wrapperRef.current) wrapperRef.current.style.backgroundColor = '';
            return;
        }

        const wrapper = wrapperRef.current;
        const container = cardsContainerRef.current;
        if (!wrapper || !container) return;

        const scrollEl = getScrollParent(wrapper);
        if (!scrollEl) return;

        const getNavH = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-height'));

        const computeOverflows = () => {
            const cH = container.offsetHeight;
            return services.map((_, i) => {
                const contentEl = contentRefs.current[i];
                if (!contentEl) return 0;
                const available = cH - i * CARD_TOP_OFFSET;
                return Math.max(0, contentEl.offsetHeight - available);
            });
        };

        const animate = () => {
            const navH = getNavH();
            const wrapperTop = wrapper.getBoundingClientRect().top;
            const scrolled = Math.max(0, -(wrapperTop - navH));
            const viewH = window.innerHeight - navH;
            const cH = container.offsetHeight;
            const slideInPx = viewH * SLIDE_IN_VH;
            const overflows = computeOverflows();

            /* Check if card container is actually in the viewport */
            const containerRect = container.getBoundingClientRect();
            const cardsInView = containerRect.top < window.innerHeight && containerRect.bottom > 0;

            let cursor = 0;
            let newActiveCard = -1;
            const slidePs: number[] = [0]; // card 0 has no slide-in

            // First pass: compute slideP for each card and apply transforms
            cardRefs.current.forEach((card, i) => {
                if (!card) return;
                const contentEl = contentRefs.current[i];
                const overflow = overflows[i];

                if (i === 0) {
                    card.style.transform = 'translateY(0) scale(1)';
                    card.style.opacity = '1';
                    if (contentEl) {
                        if (overflow > 0) {
                            const p = Math.max(0, Math.min(1, scrolled / overflow));
                            contentEl.style.transform = `translateY(${-p * overflow}px)`;
                        } else {
                            contentEl.style.transform = '';
                        }
                    }
                    cursor = overflow;
                    if (cardsInView) newActiveCard = 0;
                } else {
                    const slideStart = cursor;
                    const slideP = Math.max(0, Math.min(1, (scrolled - slideStart) / slideInPx));
                    slidePs[i] = slideP;

                    const scale = 0.96 + slideP * 0.04;
                    card.style.transform = `translateY(${(1 - slideP) * cH}px) scale(${scale})`;

                    const scrollStart = slideStart + slideInPx;
                    if (contentEl) {
                        if (overflow > 0) {
                            const p = Math.max(0, Math.min(1, (scrolled - scrollStart) / overflow));
                            contentEl.style.transform = `translateY(${-p * overflow}px)`;
                        } else {
                            contentEl.style.transform = '';
                        }
                    }

                    if (cardsInView && slideP >= 0.85) {
                        newActiveCard = i;
                    }

                    cursor = scrollStart + overflow;
                }
            });

            // Second pass: apply dynamic side insets
            // For card i, inset = sum of slideP of all cards above it * CARD_SIDE_INSET
            cardRefs.current.forEach((card, i) => {
                if (!card) return;
                let inset = 0;
                for (let j = i + 1; j < services.length; j++) {
                    inset += (slidePs[j] ?? 0) * CARD_SIDE_INSET;
                }
                card.style.left = `${inset}px`;
                card.style.right = `${inset}px`;
            });

            /* Toggle data-active for CSS stagger animations */
            if (newActiveCard !== activeCardRef.current) {
                cardRefs.current.forEach((card, i) => {
                    if (!card) return;
                    if (i === newActiveCard) {
                        card.dataset.active = 'true';
                    } else {
                        delete card.dataset.active;
                    }
                });
                activeCardRef.current = newActiveCard;
            }

            /* Scroll-driven background color transition */
            const totalScroll = cursor;
            if (totalScroll > 0) {
                const progress = Math.max(0, Math.min(1, scrolled / totalScroll));
                const ci = progress * (WRAPPER_BG_COLORS.length - 1);
                const lo = Math.floor(ci);
                const hi = Math.min(lo + 1, WRAPPER_BG_COLORS.length - 1);
                wrapper.style.backgroundColor = lerpColor(WRAPPER_BG_COLORS[lo], WRAPPER_BG_COLORS[hi], ci - lo);
            }
        };

        const measure = () => {
            const cH = container.offsetHeight;
            const viewH = window.innerHeight - getNavH();
            const slideInPx = viewH * SLIDE_IN_VH;
            const overflows = computeOverflows();

            let total = overflows[0];
            for (let i = 1; i < services.length; i++) {
                total += slideInPx + overflows[i];
            }

            setContainerHeight(cH);
            setWrapperHeight(`${total + viewH}px`);
            animate();
        };

        measure();
        scrollEl.addEventListener('scroll', animate, { passive: true });
        window.addEventListener('resize', measure);
        return () => {
            scrollEl.removeEventListener('scroll', animate);
            window.removeEventListener('resize', measure);
        };
    }, [isDesktop]);

    return (
        <>
            {/* ─── Desktop: Scroll-Driven Stacking Cards ─── */}
            <div className="hidden pb-24 lg:block">
                {/* Tall wrapper — provides scroll distance for card animation */}
                <div
                    ref={wrapperRef}
                    style={{
                        height: wrapperHeight,
                        backgroundColor: `rgb(${WRAPPER_BG_COLORS[0].join(', ')})`,
                    }}
                >
                    {/* Tall screens: entire section (header + cards) is sticky */}
                    {/* Short screens: only cards section is sticky */}
                    <div
                        className={isTallScreen ? 'sticky' : 'contents'}
                        style={isTallScreen ? { top: 'var(--nav-height, 76px)' } : undefined}
                    >
                        {/* Section Header */}
                        <div className="px-16 pb-6 pt-10 xl:px-20 2xl:pb-12 2xl:pt-24">
                            <div className="flex flex-row items-end gap-16">
                                <AnimateOnScroll>
                                    <h2 className="text-secondary max-w-2xl text-[44px] font-extrabold leading-[1.1] tracking-tight xl:text-[56px] 2xl:text-[64px]">
                                        {t('services.heading')}
                                    </h2>
                                </AnimateOnScroll>
                                <AnimateOnScroll delay={200}>
                                    <p className="text-[#4E4D4D] max-w-3xl text-[15px] font-medium leading-[2]">
                                        {t('services.subtitle')}
                                    </p>
                                </AnimateOnScroll>
                            </div>
                        </div>

                        {/* Cards section */}
                        <div
                            className={`flex items-center overflow-hidden px-16 xl:px-20 ${!isTallScreen ? 'sticky' : ''}`}
                            style={
                                !isTallScreen
                                    ? { top: 'calc(var(--nav-height, 76px) + 44px)', marginBottom: 200 }
                                    : undefined
                            }
                        >
                            <div ref={cardsContainerRef} className="relative h-[500px] w-full overflow-hidden">
                                {services.map((service, i) => (
                                    <div
                                        key={i}
                                        ref={(el) => {
                                            cardRefs.current[i] = el;
                                        }}
                                        className="absolute will-change-transform"
                                        style={{
                                            top: `${i * CARD_TOP_OFFSET}px`,
                                            left: 0,
                                            right: 0,
                                            zIndex: i + 1,
                                        }}
                                    >
                                        <div
                                            className="relative overflow-hidden rounded-[10px]"
                                            style={{
                                                backgroundColor: service.bgColor,
                                                height:
                                                    containerHeight > 0
                                                        ? `${containerHeight - i * CARD_TOP_OFFSET}px`
                                                        : undefined,
                                                boxShadow: i > 0 ? '0 -8px 30px rgba(0, 0, 0, 0.06)' : undefined,
                                            }}
                                        >
                                            {/* <div
                                                className="pointer-events-none absolute inset-x-0 top-0 rounded-t-[10px]"
                                                style={{
                                                    backgroundColor: service.bgColor,
                                                    height: `${CARD_TOP_OFFSET}px`,
                                                    zIndex: 2,
                                                }}
                                            /> */}
                                            <div
                                                ref={(el) => {
                                                    contentRefs.current[i] = el;
                                                }}
                                                className="flex h-full flex-row will-change-transform"
                                            >
                                                {/* Left Content */}
                                                <div className="flex max-w-[45%] flex-1 flex-col justify-center px-12 py-10">
                                                    <div className="svc-stagger mb-6 flex items-center gap-3">
                                                        <img
                                                            src={service.logo}
                                                            alt={`${service.brandPrefix} ${service.name} icon`}
                                                            className="h-14 w-14 flex-shrink-0 rounded-xl object-contain"
                                                        />
                                                        <h3 className="text-[40px] font-extrabold leading-[1.1] tracking-tight xl:text-[48px]">
                                                            <span className="text-secondary">
                                                                {service.brandPrefix}{' '}
                                                            </span>
                                                            <span className="text-[#C2C2C2]">{service.name}</span>
                                                        </h3>
                                                    </div>

                                                    <p className="svc-stagger svc-stagger-1 text-secondary mb-8 max-w-md text-[15px] leading-[2] font-medium">
                                                        {t(`services.${i}.description`)}
                                                    </p>

                                                    <div className="svc-stagger svc-stagger-2 flex items-center gap-4 font-bold">
                                                        <a
                                                            href={service.ctaLink}
                                                            className="bg-secondary hover:bg-secondary-400 rounded-full px-8 py-3.5 text-[15px] font-medium text-white transition-all duration-200 hover:scale-[1.03] hover:shadow-lg active:scale-[0.98]"
                                                        >
                                                            {t(`services.${i}.cta`)}
                                                        </a>
                                                    </div>
                                                </div>

                                                {/* Center Lines Decoration — top-aligned on odd cards, bottom-aligned on even */}
                                                <div
                                                    className={`svc-stagger svc-stagger-3 flex w-[258px] flex-shrink-0 justify-center ${i % 2 === 0 ? 'mt-[-60px] items-start' : 'mb-[-60px] items-end'}`}
                                                >
                                                    <img
                                                        src="/assets/libs/lines.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        className="h-[70%] object-contain opacity-40"
                                                    />
                                                </div>

                                                {/* Right Content — Preview Card */}
                                                <div className="svc-stagger svc-stagger-4 flex flex-1 flex-col items-center justify-center px-8 py-10">
                                                    <div className="w-full max-w-md transition-shadow duration-300">
                                                        <img
                                                            src={service.image}
                                                            alt={`${service.brandPrefix} ${service.name} preview`}
                                                            className="w-full rounded-lg object-contain"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Mobile: Horizontal Snap Carousel ─── */}
            <section className="px-4 py-10 lg:hidden">
                {/* Section Header */}
                <div className="pb-8">
                    <AnimateOnScroll>
                        <h2 className="text-secondary mb-4 text-[28px] font-bold leading-[1.1] tracking-tight sm:text-[36px]">
                            {t('services.heading')}
                        </h2>
                    </AnimateOnScroll>
                    <AnimateOnScroll delay={150}>
                        <p className="text-secondary text-base font-normal leading-relaxed">{t('services.subtitle')}</p>
                    </AnimateOnScroll>
                </div>

                <MobileCarousel />
            </section>
        </>
    );
}
